import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import {
  Sparkles, ScrollText, ShieldCheck, Globe2, Zap, FileCheck2,
  ArrowRight, CheckCircle2, Users, Lock, Star, Quote, Play, X,
} from "lucide-react";
import { t, LOCALES } from "@/locales/landing";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { NvcLandingTeaser } from "@/components/NvcNoticeIntake";
import { DocumentChecklistCompact } from "@/components/DocumentChecklistCard";
import WidgetPromoSection from "@/components/WidgetPromoSection";

/**
 * Modern SaaS landing page for DS-260USA.com. Public route at `/` for
 * unauthenticated visitors. Logged-in users are redirected to the Dashboard.
 *
 * Design language: bold hero with gradient accents, product screenshots slot,
 * social proof, feature grid, testimonial band, security band, and a
 * conversion CTA at the bottom. Uses existing color palette + fonts.
 */
// Formats "City, ST · relationship" once per testimonial card.
function formatLocation(t) {
  const loc = [t.city, t.state].filter(Boolean).join(", ");
  return t.relationship ? `${loc}${loc ? " · " : ""}${t.relationship}` : loc;
}

const HERO_IMG =
  "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80";

export default function LandingPage({ locale = "en" } = {}) {
  const { user } = useAuth();


  // Set document lang + inject hreflang alternates for SEO. Runs on every
  // route mount so switching /es → /hi updates <html lang> too.
  useEffect(() => {
    document.documentElement.lang = locale;
    const meta = LOCALES.find((l) => l.code === locale);
    document.documentElement.dir = meta?.dir || "ltr";
    // Update title tag with the localized headline for SEO relevance.
    document.title = `DS-260USA.com — ${t("hero_title", locale).slice(0, 60)}`;
    // Insert / update hreflang alternates. We nuke prior tags we own and
    // reinsert so back/forward navigation stays consistent.
    document.querySelectorAll("link[data-gcu-hreflang]").forEach((n) => n.remove());
    const origin = window.location.origin;
    for (const l of LOCALES) {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = l.code;
      link.href = l.code === "en" ? `${origin}/` : `${origin}/${l.code}`;
      link.dataset.gcuHreflang = "1";
      document.head.appendChild(link);
    }
    // x-default → English
    const x = document.createElement("link");
    x.rel = "alternate"; x.hreflang = "x-default"; x.href = `${origin}/`;
    x.dataset.gcuHreflang = "1";
    document.head.appendChild(x);
    return () => {
      document.querySelectorAll("link[data-gcu-hreflang]").forEach((n) => n.remove());
    };
  }, [locale]);

  if (user === null) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-app)" }}>
      <TopBar locale={locale} />
      <Hero locale={locale} />
      <NvcLandingTeaser />
      <SocialProofBand />
      <MultilingualSection />
      <FeatureGrid />
      <HowItWorks />
      <TheDifference />
      <CredibilitySection />
      <YouReviewSection />
      <SecurityBand />
      <WidgetPromoSection />
      <FinalCTA />
      <MarketingFooter locale={locale} />
    </div>
  );
}

function TopBar({ locale = "en" }) {
  return (
    <header
      className="sticky top-0 z-30 backdrop-blur-md border-b"
      style={{ borderColor: "var(--border-stone)", background: "rgba(249,248,245,0.90)" }}
      data-testid="landing-topbar"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3 flex items-center justify-between">
        <Link to={locale === "en" ? "/" : `/${locale}`} className="flex items-center gap-2.5" data-testid="landing-logo">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: "var(--action)" }}
          >
            <ScrollText className="w-4 h-4 text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-[16px]" style={{ color: "var(--text-primary)" }}>
              DS-260USA<span style={{ color: "var(--action)" }}>.com</span>
            </div>
            <div className="text-[10.5px] uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
              Consular Processing
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/pricing"
            data-testid="landing-topbar-pricing-link"
            className="hidden md:inline text-sm font-medium hover:opacity-80"
            style={{ color: "var(--action)" }}
          >
            Pricing
          </Link>
          <LanguageSwitcher current={locale} />
          <Link to="/login" data-testid="landing-signin-link" className="btn-ghost text-sm">
            {t("nav_signin", locale)}
          </Link>
          <Link to="/register" data-testid="landing-getstarted-btn" className="btn-primary text-sm">
            {t("nav_get_started", locale)}
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero({ locale = "en" }) {
  return (
    <section className="relative overflow-hidden">
      {/* Gradient blob */}
      <div
        className="absolute -top-32 -right-40 w-[520px] h-[520px] rounded-full opacity-20 blur-3xl"
        style={{ background: "radial-gradient(circle, #C85A32 0%, transparent 70%)" }}
      />
      <div
        className="absolute -bottom-40 -left-32 w-[480px] h-[480px] rounded-full opacity-15 blur-3xl"
        style={{ background: "radial-gradient(circle, #FDF8F0 0%, transparent 70%)" }}
      />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-10 pb-14 sm:pt-24 sm:pb-28 relative">
        <div className="grid lg:grid-cols-12 gap-8 sm:gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="pill pill-ai mb-5" data-testid="landing-hero-pill">
              <Sparkles className="w-3.5 h-3.5" />
              {t("pill_new", locale)}
            </div>
            <h1
              className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] font-extrabold mb-6"
              style={{ color: "var(--text-primary)" }}
              data-testid="landing-hero-heading"
            >
              {t("hero_title", locale)}
            </h1>
            <p
              className="text-[17px] sm:text-lg leading-relaxed max-w-2xl mb-8"
              style={{ color: "var(--text-secondary)" }}
              data-testid="landing-hero-subtitle"
            >
              {t("hero_subtitle", locale)}
            </p>
            <div className="flex flex-wrap items-center gap-3 mb-10">
              <Link
                to="/register"
                data-testid="landing-hero-cta"
                className="btn-primary flex items-center gap-2 text-base px-5 py-3"
              >
                {t("cta_primary", locale)}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/pricing"
                data-testid="landing-hero-demo-btn"
                className="btn-ghost text-sm flex items-center gap-1.5"
                style={{
                  border: "1px solid var(--border-stone)",
                  background: "#FFFFFF",
                  padding: "0.7rem 1.15rem",
                }}
              >
                <Sparkles className="w-4 h-4" style={{ color: "var(--action)" }} />
                {t("cta_secondary", locale)}
              </Link>
            </div>
            <div className="max-w-lg mb-6" data-testid="landing-hero-doc-checklist">
              <DocumentChecklistCompact />
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs" style={{ color: "var(--text-secondary)" }}>
              <span className="flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5" /> Save Hours of Paperwork
              </span>
              <span className="flex items-center gap-1.5" data-testid="landing-languages-pill">
                <Globe2 className="w-3.5 h-3.5" /> Available in Multiple Languages
              </span>
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> Optional Professional Review
              </span>
              <span className="flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" /> Human Assistance Available
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> You Review Before Filing
              </span>
            </div>
            <div className="mt-4 text-[11px]" style={{ color: "var(--text-secondary)" }} data-testid="landing-trust-bar">
              Designed by a licensed U.S. immigration attorney with 35+ years of experience · Not a law firm.
            </div>
          </div>
          <div className="lg:col-span-5">
            <HeroVideoWidget />
            <div className="mt-4 grid grid-cols-3 gap-3">
              <StatChip value="DS-260" label="for every family member" />
              <StatChip value="20+" label="languages supported" />
              <StatChip value="You" label="review before filing" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroVideoWidget() {
  const [videoUrl, setVideoUrl] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/site-config/public")
      .then(({ data }) => { if (!cancelled) setVideoUrl(data?.hero_video_url || ""); })
      .catch(() => { /* fall through — no video yet, still show the play card */ });
    return () => { cancelled = true; };
  }, []);

  const openPlayer = () => setModalOpen(true);

  return (
    <>
      <button
        type="button"
        onClick={openPlayer}
        className="card-surface p-1.5 shadow-2xl anim-in relative overflow-hidden group text-left w-full"
        style={{ boxShadow: "0 25px 60px -20px rgba(26,46,36,0.25)" }}
        data-testid="hero-video-widget"
        aria-label="Play the DS-260USA.com walkthrough"
      >
        <img
          src={HERO_IMG}
          alt="DS-260USA.com — how it works"
          className="w-full h-[280px] sm:h-[440px] object-cover rounded-lg"
        />
        <div
          className="absolute inset-1.5 rounded-lg flex flex-col items-center justify-center transition-all"
          style={{ background: "linear-gradient(180deg, rgba(26,46,36,0.10) 0%, rgba(26,46,36,0.55) 100%)" }}
        >
          <div
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center shadow-2xl transition-transform group-hover:scale-110"
            style={{ background: "var(--action)" }}
          >
            <Play className="w-9 h-9 sm:w-10 sm:h-10 text-white ml-1.5" fill="white" />
          </div>
          <div className="mt-4 text-white font-display font-bold text-lg sm:text-xl drop-shadow-lg">
            Watch a 2-minute walkthrough
          </div>
          <div className="mt-1 text-white/90 text-xs sm:text-sm drop-shadow-lg">
            See how a real family prepares their DS-260s
          </div>
        </div>
      </button>
      {modalOpen && (
        <HeroVideoModal videoUrl={videoUrl} onClose={() => setModalOpen(false)} />
      )}
    </>
  );
}

function HeroVideoModal({ videoUrl, onClose }) {
  const isYouTube = /(?:youtube\.com|youtu\.be)/.test(videoUrl || "");
  const isVimeo = /vimeo\.com/.test(videoUrl || "");
  const isLoom = /loom\.com/.test(videoUrl || "");
  const isMp4 = /\.(mp4|webm|mov)(\?|$)/i.test(videoUrl || "");
  const embed = normalizeEmbed(videoUrl);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(26,46,36,0.75)" }}
      onClick={onClose}
      data-testid="hero-video-modal"
    >
      <div
        className="card-surface w-full max-w-4xl p-1.5 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-3 -right-3 w-9 h-9 rounded-full flex items-center justify-center shadow-lg"
          style={{ background: "#FFFFFF" }}
          aria-label="Close"
          data-testid="hero-video-close"
        >
          <X className="w-4 h-4" />
        </button>
        <VideoBody videoUrl={videoUrl} isMp4={isMp4} isEmbeddable={isYouTube || isVimeo || isLoom} embed={embed} onClose={onClose} />
      </div>
    </div>
  );
}

function VideoBody({ videoUrl, isMp4, isEmbeddable, embed, onClose }) {
  if (!videoUrl) return <VideoPlaceholder onClose={onClose} />;
  if (isMp4) {
    return (
      <video
        src={videoUrl}
        controls
        autoPlay
        className="w-full rounded-md"
        style={{ aspectRatio: "16 / 9" }}
        data-testid="hero-video-player"
      />
    );
  }
  if (isEmbeddable) {
    return (
      <iframe
        title="DS-260USA.com walkthrough"
        src={embed}
        className="w-full rounded-md"
        style={{ aspectRatio: "16 / 9", border: 0 }}
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
        data-testid="hero-video-iframe"
      />
    );
  }
  return <VideoPlaceholder onClose={onClose} note="We couldn't recognize that video URL format. Ask an admin to check it." />;
}

function VideoPlaceholder({ onClose, note }) {
  return (
    <div className="p-8 text-center" data-testid="hero-video-placeholder">
      <div
        className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4"
        style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-border)" }}
      >
        <Sparkles className="w-7 h-7" style={{ color: "var(--ai-text)" }} />
      </div>
      <h3 className="font-display font-bold text-xl mb-2" style={{ color: "var(--text-primary)" }}>
        Video coming soon
      </h3>
      <p className="text-sm mb-5 max-w-md mx-auto" style={{ color: "var(--text-secondary)" }}>
        {note || "In the meantime, start your own case — upload your family's documents and watch the DS-260s take shape."}
      </p>
      <div className="flex items-center justify-center gap-3">
        <Link to="/register" onClick={onClose} className="btn-primary inline-flex items-center gap-2">
          <Play className="w-4 h-4" fill="currentColor" />
          Start my case
        </Link>
        <button type="button" onClick={onClose} className="btn-ghost text-sm">Close</button>
      </div>
    </div>
  );
}

function normalizeEmbed(url) {
  if (!url) return "";
  // youtu.be/<id> or youtube.com/watch?v=<id>  →  youtube.com/embed/<id>
  const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`;
  // vimeo.com/<id>  →  player.vimeo.com/video/<id>
  const vMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vMatch) return `https://player.vimeo.com/video/${vMatch[1]}?autoplay=1`;
  // loom.com/share/<id>  →  loom.com/embed/<id>
  const lMatch = url.match(/loom\.com\/(?:share|embed)\/([\w-]+)/);
  if (lMatch) return `https://www.loom.com/embed/${lMatch[1]}?autoplay=true`;
  return url; // best-effort fallback
}

function StatChip({ value, label }) {
  return (
    <div
      className="card-surface p-3 text-center"
      style={{ background: "rgba(255,255,255,0.85)" }}
    >
      <div className="font-display font-bold text-lg" style={{ color: "var(--action)" }}>
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wider mt-0.5" style={{ color: "var(--text-secondary)" }}>
        {label}
      </div>
    </div>
  );
}

function SocialProofBand() {
  return (
    <section
      className="border-y py-6"
      style={{ borderColor: "var(--border-stone)", background: "#FFFFFF" }}
      data-testid="landing-attorney-credibility-band"
    >
      <div className="mx-auto max-w-6xl px-6">
        <div className="flex flex-col md:flex-row items-center gap-4 justify-center text-center md:text-left">
          <div className="text-[10px] uppercase tracking-widest whitespace-nowrap" style={{ color: "var(--accent-copper)" }}>
            Designed by an attorney, not a startup
          </div>
          <div className="hidden md:block h-6 w-px" style={{ background: "var(--border-stone)" }} />
          <div className="text-sm max-w-2xl" style={{ color: "var(--text-primary)" }}>
            Built by a <strong>Licensed U.S. Immigration Attorney</strong>, admitted to practice in 1980, with over{" "}
            <strong>35 years of experience</strong> handling U.S. immigration cases exclusively.
          </div>
        </div>
      </div>
    </section>
  );
}

function PressBadge({ label }) {
  return (
    <div
      className="text-sm font-display font-semibold tracking-tight"
      style={{ color: "var(--text-primary)" }}
    >
      {label}
    </div>
  );
}

function TestimonialsSection({ locale = "en" }) {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/testimonials")
      .then(({ data }) => { if (!cancelled) { setRows(data); setLoaded(true); } })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  // For non-English locales we prepend a hand-translated hero testimonial so
  // the visitor gets social proof in THEIR language above the fold — even
  // when there are no English-authored real testimonials to display below.
  const inLanguageTestimonial = locale !== "en" ? {
    id: `__hero-${locale}`,
    consented_at: null,
    author_name: t("testimonial_name", locale).replace(/^—\s*/, ""),
    author_location: "",
    body: t("testimonial_body", locale).replace(/^"|"$/g, ""),
    localized: true,
  } : null;

  const shown = inLanguageTestimonial
    ? [inLanguageTestimonial, ...rows].slice(0, 3)
    : rows.slice(0, 3);

  if (!loaded || shown.length === 0) return null;

  return (
    <section className="py-16 sm:py-20 border-b"
             style={{ background: "#FFFFFF", borderColor: "var(--border-stone)" }}
             data-testid="landing-testimonials">
      <div className="mx-auto max-w-6xl px-6">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="pill pill-ai mb-3 inline-flex">Real families, real cases</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold" style={{ color: "var(--text-primary)" }}>
            {t("testimonial_title", locale)}
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {shown.map((row) => (
            <TestimonialCard key={row.id} t={row} />
          ))}
        </div>
        {rows.length > shown.length && (
          <div className="text-center text-xs mt-6" style={{ color: "var(--text-secondary)" }}>
            +{rows.length - (shown.length - (inLanguageTestimonial ? 1 : 0))} more testimonials from families across the U.S.
          </div>
        )}
      </div>
    </section>
  );
}

function TestimonialCard({ t }) {
  return (
    <div className="card-surface p-6 flex flex-col" data-testid={`testimonial-${t.id}`}>
      <Quote className="w-6 h-6 mb-3 opacity-40" style={{ color: "var(--action)" }} />
      <div className="flex items-center gap-0.5 mb-3">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} className="w-4 h-4"
                style={{ color: n <= (t.rating || 5) ? "#E5B93B" : "#E5E3DC",
                         fill: n <= (t.rating || 5) ? "#F6C244" : "transparent" }} />
        ))}
      </div>
      <p className="text-[14px] leading-relaxed italic flex-1"
         style={{ color: "var(--text-primary)" }}>
        &ldquo;{t.quote}&rdquo;
      </p>
      <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--border-stone)" }}>
        <div className="font-display font-bold text-sm" style={{ color: "var(--text-primary)" }}>
          {t.display_name}
        </div>
        <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
          {formatLocation(t)}
        </div>
      </div>
    </div>
  );
}

const FEATURES = [
  {
    icon: Globe2,
    title: "Available in multiple languages",
    body:
      "Complete your case in the language you're most comfortable using. Upload documents in their original language — Spanish, 中文, हिन्दी, Tagalog, Tiếng Việt, and more.",
  },
  {
    icon: ScrollText,
    title: "Simple document upload — with instant quality feedback",
    body:
      "Snap a photo of a passport, birth certificate, or marriage certificate from anywhere. We rate every scan for readability the moment it's uploaded — if the lighting is poor, the paper is folded, or a corner is cut off, we tell you before NVC rejects it. Upload the clearest copies you have.",
  },
  {
    icon: FileCheck2,
    title: "Interview-ready consular packet",
    body:
      "Every family member's DS-260 drafted, civil documents organized by NVC's own checklist, and fee steps tracked — from the NVC fee bill to the consulate interview.",
  },
  {
    icon: ShieldCheck,
    title: "Optional professional review",
    body:
      "Invite a licensed immigration attorney to review your case in a read-only workspace. They can flag anything they want changed before you file — you stay in control.",
  },
  {
    icon: Lock,
    title: "Secure document protection",
    body:
      "Documents are encrypted at rest and in transit. Only you (and any attorney you invite) can see them. Delete anytime — no dark-pattern retention.",
  },
  {
    icon: Users,
    title: "For spouses, parents, children & siblings",
    body:
      "Petition your spouse, parent, unmarried children, or siblings. One case per beneficiary — with a one-click shortcut to reuse petitioner details across siblings.",
  },
];

function FeatureGrid() {
  return (
    <section id="features" className="py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="pill pill-ai mb-4 inline-flex">Built for families</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold mb-4" style={{ color: "var(--text-primary)" }}>
            Purpose-built for family-based petitions.
          </h2>
          <p className="text-[16px]" style={{ color: "var(--text-secondary)" }}>
            Everything you need to prepare and file a family-based petition — and
            nothing you don't. Documents stay in your hands. You review every field
            before anything is filed.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="card-surface p-6"
              data-testid={`landing-feature-${f.title.toLowerCase().split(" ")[0]}`}
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-border)" }}
              >
                <f.icon className="w-5 h-5" style={{ color: "var(--ai-text)" }} />
              </div>
              <h3 className="font-display font-bold text-base mb-2" style={{ color: "var(--text-primary)" }}>
                {f.title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                {f.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const HOW_STEPS = [
  {
    n: "01",
    title: "Snap or upload your documents",
    body: "Passports, birth certificates, marriage certificates, IDs — in any of 15+ languages. Take pictures with your phone or drag files in from your computer. No typing required.",
  },
  {
    n: "02",
    title: "We read every document for you",
    body: "DS-260USA.com extracts names, dates, places of birth, and relationships directly from your documents — then creates each family member's record and auto-populates their DS-260. You don't retype anything.",
  },
  {
    n: "03",
    title: "Review, approve, and submit",
    body: "Every field is shown next to the document it came from, so you can double-check in seconds. Fix anything you want, request optional attorney review, then enter your answers at ceac.state.gov with confidence.",
  },
];

function HowItWorks() {
  return (
    <section
      id="how-it-works"
      className="py-20 sm:py-24 border-t"
      style={{ background: "#FFFFFF", borderColor: "var(--border-stone)" }}
      data-testid="landing-how-it-works-section"
    >
      <div className="mx-auto max-w-6xl px-6">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="pill pill-ai mb-4 inline-flex">How it works</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold" style={{ color: "var(--text-primary)" }}>
            From document photos to interview-ready DS-260s — in minutes, not weeks.
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            Stop typing. Start uploading. Your documents already contain 90% of what
            the DS-260 asks for — we pull the information from them so you don't
            have to key it in over and over, family member after family member.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {HOW_STEPS.map((s) => (
            <div key={s.n} className="relative" data-testid={`how-step-${s.n}`}>
              <div
                className="font-display font-extrabold text-5xl mb-3"
                style={{ color: "var(--action)", opacity: 0.85 }}
              >
                {s.n}
              </div>
              <h3 className="font-display font-bold text-lg mb-2" style={{ color: "var(--text-primary)" }}>
                {s.title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                {s.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- The DS-260USA.com Difference (Compare section) ----------
const DIFFERENCE_ROWS = [
  {
    step: "Getting started",
    old: "Read a 12-page instruction PDF. Guess which form applies to you.",
    ours: "Answer one question — who are you sponsoring? We pick the right form automatically.",
  },
  {
    step: "Entering family information",
    old: "Retype every name, date, and place of birth from each document — usually twice.",
    ours: "Upload the document once. We read it and fill the forms for you.",
  },
  {
    step: "Foreign-language documents",
    old: "Pay a translator, wait days, retype the results into English on the form.",
    ours: "Upload in the original language. We read 15+ languages including Korean, Mandarin, Hindi, Spanish, Vietnamese, and Tagalog.",
  },
  {
    step: "Catching mistakes",
    old: "Hope you didn't fat-finger a date or misspell a name across four family members' forms.",
    ours: "Every field is shown next to the source document — mismatches are flagged before you submit.",
  },
  {
    step: "The consular packet",
    old: "Chase NVC's document checklist yourself, country by country, applicant by applicant.",
    ours: "Every family member's DS-260 drafted, civil documents organized to NVC's checklist, fee steps tracked to the interview.",
  },
  {
    step: "Optional attorney review",
    old: "$1,500–$4,000 flat fee, then you still do the paperwork.",
    ours: "Invite a licensed U.S. immigration attorney to review your finished case in read-only mode. Pay only if you want the review.",
  },
];

function TheDifference() {
  return (
    <section
      id="compare"
      className="py-20 sm:py-24"
      style={{ background: "var(--bg-app)" }}
      data-testid="landing-difference-section"
    >
      <div className="mx-auto max-w-6xl px-6">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="pill pill-ai mb-4 inline-flex">The DS-260USA.com difference</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold" style={{ color: "var(--text-primary)" }}>
            Stop typing forms. Start uploading documents.
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            Traditional form-fillers ask you to become a data-entry clerk. We do
            the opposite — you photograph your documents, we build the case.
          </p>
        </div>

        {/* Desktop / tablet table */}
        <div
          className="hidden md:block card-surface overflow-hidden"
          style={{ background: "#FFFFFF" }}
        >
          <div
            className="grid grid-cols-12 px-6 py-4 text-xs font-semibold uppercase tracking-wide"
            style={{
              background: "#FBF6EC",
              borderBottom: "1px solid var(--border-stone)",
              color: "var(--text-secondary)",
            }}
          >
            <div className="col-span-3">Step</div>
            <div className="col-span-4">The old way (form-fillers)</div>
            <div className="col-span-5" style={{ color: "var(--action)" }}>
              DS-260USA.com
            </div>
          </div>
          {DIFFERENCE_ROWS.map((row, i) => (
            <div
              key={row.step}
              className="grid grid-cols-12 px-6 py-5 text-sm"
              style={{
                borderBottom:
                  i === DIFFERENCE_ROWS.length - 1
                    ? "none"
                    : "1px solid var(--border-stone)",
              }}
              data-testid={`difference-row-${i}`}
            >
              <div
                className="col-span-3 font-semibold pr-4"
                style={{ color: "var(--text-primary)" }}
              >
                {row.step}
              </div>
              <div
                className="col-span-4 pr-4 leading-relaxed"
                style={{ color: "var(--text-secondary)" }}
              >
                {row.old}
              </div>
              <div
                className="col-span-5 leading-relaxed flex gap-2 items-start"
                style={{ color: "var(--text-primary)" }}
              >
                <CheckCircle2
                  className="w-4 h-4 flex-shrink-0 mt-0.5"
                  style={{ color: "#065F46" }}
                />
                <span>{row.ours}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile stacked cards */}
        <div className="md:hidden space-y-4">
          {DIFFERENCE_ROWS.map((row, i) => (
            <div
              key={row.step}
              className="card-surface p-5"
              style={{ background: "#FFFFFF" }}
              data-testid={`difference-card-${i}`}
            >
              <div
                className="text-xs font-semibold uppercase tracking-wide mb-3"
                style={{ color: "var(--text-secondary)" }}
              >
                {row.step}
              </div>
              <div className="mb-3">
                <div
                  className="text-[11px] font-semibold uppercase tracking-wide mb-1"
                  style={{ color: "var(--text-secondary)" }}
                >
                  The old way
                </div>
                <div className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                  {row.old}
                </div>
              </div>
              <div>
                <div
                  className="text-[11px] font-semibold uppercase tracking-wide mb-1"
                  style={{ color: "var(--action)" }}
                >
                  DS-260USA.com
                </div>
                <div
                  className="text-sm leading-relaxed flex gap-2 items-start"
                  style={{ color: "var(--text-primary)" }}
                >
                  <CheckCircle2
                    className="w-4 h-4 flex-shrink-0 mt-0.5"
                    style={{ color: "#065F46" }}
                  />
                  <span>{row.ours}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/register"
            data-testid="landing-difference-cta"
            className="btn-primary inline-flex items-center gap-2 text-base px-6 py-3"
          >
            Try it free — upload your first document
            <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-xs mt-3" style={{ color: "var(--text-secondary)" }}>
            Free to start. You only pay when you're ready to download your filing packet.
          </p>
        </div>
      </div>
    </section>
  );
}

function SecurityBand() {
  const points = [
    "Documents encrypted at rest and in transit — same practices used by top-tier banks",
    "Only you and any attorney you invite can see your case",
    "Session cookies expire automatically — no tokens sitting in your browser storage",
    "Delete your case anytime — you own your data, always",
  ];
  return (
    <section className="py-20" style={{ background: "var(--bg-app)" }}>
      <div className="mx-auto max-w-6xl px-6">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="pill pill-ai mb-4 inline-flex">Secure document protection</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold mb-4" style={{ color: "var(--text-primary)" }}>
              Your documents stay with you.
            </h2>
            <p className="text-[15px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              Immigration paperwork is among the most sensitive information a family
              ever shares online. We treat it that way.
            </p>
            <Link
              to="/security"
              className="text-sm font-semibold mt-5 inline-flex items-center gap-1"
              style={{ color: "var(--action)" }}
              data-testid="landing-security-link"
            >
              Read our security overview <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <ul className="space-y-3">
            {points.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm" style={{ color: "var(--text-primary)" }}>
                <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "#065F46" }} />
                {p}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ---------- NEW: Multilingual Section (elevate to top of features) ----------
function MultilingualSection() {
  const languages = [
    { flag: "🌍", name: "English" },
    { flag: "🌎", name: "Español" },
    { flag: "🌏", name: "中文" },
    { flag: "🌍", name: "हिन्दी" },
    { flag: "🌎", name: "Tagalog" },
    { flag: "🌏", name: "Tiếng Việt" },
    { flag: "🌍", name: "한국어" },
    { flag: "🌎", name: "বাংলা" },
  ];
  return (
    <section
      className="py-20 sm:py-24 border-t"
      style={{ background: "#FFFFFF", borderColor: "var(--border-stone)" }}
      data-testid="landing-multilingual-section"
    >
      <div className="mx-auto max-w-6xl px-6">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="pill pill-ai mb-4 inline-flex items-center gap-1.5">
              <Globe2 className="w-3.5 h-3.5" /> Available in multiple languages
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold mb-4" style={{ color: "var(--text-primary)" }}>
              Immigration is complicated enough. Language shouldn't be.
            </h2>
            <p className="text-[15px] leading-relaxed mb-6" style={{ color: "var(--text-secondary)" }}>
              Use DS-260USA.com in the language you're most comfortable with.
              Whether you're helping a spouse in Mexico, parents in India, family in
              China, or relatives anywhere else in the world, you can complete your
              case with clear instructions and multilingual support.
            </p>
            <ul className="space-y-2.5">
              {[
                "Multiple languages supported",
                "Upload documents in their original language",
                "Clear guidance every step of the way",
                "Multilingual assistance available",
              ].map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm" style={{ color: "var(--text-primary)" }}>
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#065F46" }} />
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {languages.map((l) => (
              <div
                key={l.name}
                className="card-surface px-4 py-3 flex items-center gap-3"
                data-testid={`lang-tile-${l.name}`}
              >
                <span className="text-xl">{l.flag}</span>
                <span className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>{l.name}</span>
              </div>
            ))}
            <div className="col-span-2 text-xs mt-2" style={{ color: "var(--text-secondary)" }}>
              + more languages coming soon
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Why families choose (outcome-first, immediately under hero) ----------
function CredibilitySection() {
  const helps = [
    "Skip retyping — we read your documents for you",
    "Handle 15+ languages, including foreign-language originals",
    "Auto-populate each family member's DS-260 from what we extract",
    "Catch mismatches by showing each field next to its source document",
    "Organize civil documents and track NVC fee steps to the interview",
    "Invite an attorney for optional read-only review before you submit",
  ];
  return (
    <section
      className="py-20 sm:py-24"
      style={{ background: "var(--bg-app)" }}
      data-testid="landing-credibility-section"
    >
      <div className="mx-auto max-w-4xl px-6 text-center">
        <div className="pill pill-ai mb-4 inline-flex">Why families choose DS-260USA.com</div>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold mb-4" style={{ color: "var(--text-primary)" }}>
          Focus on your family, not the forms.
        </h2>
        <p className="text-[16px] leading-relaxed max-w-2xl mx-auto mb-8" style={{ color: "var(--text-secondary)" }}>
          Family immigration should be about bringing loved ones together — not
          spending weeks typing the same names and dates into a dozen boxes.
          Upload the documents you already have, and DS-260USA.com turns them
          into a filing-ready packet that you review and approve before submission.
        </p>
        <ul className="grid sm:grid-cols-2 gap-3 text-left max-w-2xl mx-auto mb-8">
          {helps.map((p) => (
            <li
              key={p}
              className="flex items-start gap-2.5 card-surface px-4 py-3 text-sm"
              style={{ color: "var(--text-primary)" }}
            >
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#065F46" }} />
              {p}
            </li>
          ))}
        </ul>
        <p className="text-sm italic" style={{ color: "var(--text-secondary)" }}>
          Help families prepare immigration paperwork more easily, more accurately,
          and with greater confidence.
        </p>
      </div>
    </section>
  );
}

// ---------- You're always in control (trust cornerstone) ----------
function YouReviewSection() {
  const items = [
    "Review every form",
    "Edit information",
    "Upload additional documents",
    "Request professional review",
    "Decide when to file",
  ];
  return (
    <section
      className="py-20 sm:py-24 border-t"
      style={{ background: "#FFFFFF", borderColor: "var(--border-stone)" }}
      data-testid="landing-you-review-section"
    >
      <div className="mx-auto max-w-4xl px-6 text-center">
        <div
          className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-5"
          style={{ background: "#EFF9F1", color: "#0F5D2E" }}
        >
          <CheckCircle2 className="w-7 h-7" />
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold mb-4" style={{ color: "var(--text-primary)" }}>
          You're always in control.
        </h2>
        <p className="text-[16px] leading-relaxed max-w-2xl mx-auto mb-6" style={{ color: "var(--text-secondary)" }}>
          Unlike fully-automated services, DS-260USA.com <strong>never submits
          anything without your knowledge</strong>. You choose what to include,
          when to review it, and when to file.
        </p>
        <ul className="grid sm:grid-cols-2 gap-2 max-w-md mx-auto text-left mb-5">
          {items.map((i) => (
            <li key={i} className="flex items-center gap-2 text-sm" style={{ color: "var(--text-primary)" }}>
              <CheckCircle2 className="w-4 h-4" style={{ color: "#065F46" }} />
              {i}
            </li>
          ))}
        </ul>
        <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
          No forms are submitted without your approval.
        </p>
        <p className="text-sm mt-4 max-w-2xl mx-auto" style={{ color: "var(--text-secondary)" }}>
          Want a second set of eyes? Invite a licensed immigration attorney to
          review your case in a read-only workspace before filing —
          <strong> optional professional review</strong> is available whenever you
          want extra peace of mind.
        </p>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-20 sm:py-24">
      <div className="mx-auto max-w-4xl px-6">
        <div
          className="card-surface p-10 sm:p-14 text-center relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #FDF8F0 0%, #FFFFFF 60%, #FDF8F0 100%)",
          }}
        >
          <div
            className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-30 blur-3xl"
            style={{ background: "radial-gradient(circle, #C85A32 0%, transparent 70%)" }}
          />
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold mb-4 relative" style={{ color: "var(--text-primary)" }}>
            Ready to start your case?
          </h2>
          <p className="text-[16px] mb-8 relative max-w-lg mx-auto" style={{ color: "var(--text-secondary)" }}>
            Sign up in 30 seconds. Free to try — you only pay when you're ready to
            download your filing packet.
          </p>
          <Link
            to="/register"
            data-testid="landing-final-cta"
            className="btn-primary inline-flex items-center gap-2 text-base px-6 py-3 relative"
          >
            Start my case
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/pricing"
            data-testid="landing-final-demo-btn"
            className="btn-ghost inline-flex items-center gap-2 text-sm ml-2 relative"
            style={{ padding: "0.75rem 1.15rem" }}
          >
            Or see pricing
          </Link>
          <p className="text-[11px] mt-4 relative" style={{ color: "var(--text-secondary)" }}>
            Free to start — you only pay when your family's DS-260s are ready.
          </p>
        </div>
      </div>
    </section>
  );
}

function MarketingFooter({ locale = "en" }) {
  return (
    <footer
      className="border-t py-10"
      style={{ borderColor: "var(--border-stone)", background: "#FFFFFF" }}
    >
      <div className="mx-auto max-w-7xl px-6 flex flex-wrap gap-4 justify-between items-center text-xs" style={{ color: "var(--text-secondary)" }}>
        <div>
          © {new Date().getFullYear()} DS-260USA.com — Consular Processing.
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="footer-language-label">{t("footer_switch", locale)}:</span>
          <LanguageSwitcher current={locale} variant="chip" />
        </div>
        <div className="flex gap-4 flex-wrap">
          <Link to="/pricing" className="hover:opacity-80">Pricing</Link>
          <Link to="/faq" className="hover:opacity-80" data-testid="footer-faq-link">FAQ</Link>
          <Link to="/sample-packet" className="hover:opacity-80" data-testid="footer-sample-packet-link">Sample packet</Link>
          <Link to="/visa-bulletin-checker" className="hover:opacity-80" data-testid="footer-bulletin-link">Visa Bulletin checker</Link>
          <Link to="/widget" className="hover:opacity-80" data-testid="footer-widget-link">Widget for attorneys</Link>
          <Link to="/press-kit" className="hover:opacity-80" data-testid="footer-press-kit-link">Press kit</Link>
          <Link to="/refer" className="hover:opacity-80" data-testid="footer-refer-link">Refer a friend</Link>
          <Link to="/affiliate" className="hover:opacity-80" data-testid="footer-affiliate-link">Affiliate program</Link>
          <Link to="/legal/terms" className="hover:opacity-80">Terms</Link>
          <Link to="/legal/privacy" className="hover:opacity-80">Privacy</Link>
          <Link to="/security" className="hover:opacity-80">Security</Link>
        </div>
      </div>
    </footer>
  );
}
